// src/app/time-attendance/components/holiday/holiday.component.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, shareReplay, takeUntil, tap } from 'rxjs/operators';
import dayjs from 'dayjs';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { HolidayService } from './services/holiday.service';
import {
  CreateHolidayBookedHoursBody,
  HolidayDepartmentOption,
  HolidayWeekOption,
} from './models/holiday.model';

type ToastKind = 'success' | 'error' | 'info';

interface HolidayFormVm {
  appliedDate: string;
  hours: number | null;
  departmentIds: number[];
}

@Component({
  selector: 'app-holiday',
  standalone: true,
  imports: [CommonModule, FormsModule, AppConfigTopNavComponent],
  templateUrl: './holiday.component.html',
  styleUrls: ['./holiday.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HolidayComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // ===== busy =====
  private readonly busy$ = new BehaviorSubject<boolean>(false);
  isBusy = false;

  // ===== toast =====
  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  // ===== confirm modal =====
  confirmOpen = false;
  confirmTitle = 'Confirm';
  confirmMessage = '';
  confirmOkText = 'Yes';
  confirmCancelText = 'No';
  confirmKind: 'default' | 'danger' = 'default';
  private confirmResolver: ((value: boolean) => void) | null = null;

  // ===== data =====
  departments$!: Observable<HolidayDepartmentOption[]>;
  weeks$!: Observable<HolidayWeekOption[]>;

  departments: HolidayDepartmentOption[] = [];
  weeks: HolidayWeekOption[] = [];

  // ===== calendar =====
  calendarMonth = dayjs().startOf('month');
  calendarCells: Array<dayjs.Dayjs | null> = [];

  isMonthPickerOpen = false;
  pickerYear = dayjs().year();
  pickerMonthIndex = dayjs().month();
  readonly maxFutureYears = 10;

  // ===== form =====
  form: HolidayFormVm = this.buildDefaultForm();
  showDepartmentPicker = false;

  // ===== ui =====
  currentYear = dayjs().year();
  selectedWeekLabel = '';
  selectedScopeLabel = 'All departments';

  constructor(
    private readonly holidayService: HolidayService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.busy$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isBusy = value;
      this.cdr.markForCheck();
    });

    this.rebuildCalendarCells();
    this.syncPickerToCalendar();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  /* =============================
     Busy helpers
  ============================= */

  private setBusy(value: boolean): void {
    this.busy$.next(value);
  }

  private withBusy<T>(obs$: Observable<T>): Observable<T> {
    this.setBusy(true);
    return obs$.pipe(
      finalize(() => this.setBusy(false)),
      takeUntil(this.destroy$),
    );
  }

  /* =============================
     Toast helpers
  ============================= */

  private showToast(message: string, kind: ToastKind = 'info', ms = 3500): void {
    this.toastMessage = message;
    this.toastKind = kind;
    this.cdr.markForCheck();

    if (this.toastTimer) clearTimeout(this.toastTimer);

    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
      this.cdr.markForCheck();
    }, ms);
  }

  clearToast(): void {
    this.toastMessage = null;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.cdr.markForCheck();
  }

  /* =============================
     Confirm helpers
  ============================= */

  private openConfirm(options: {
    title: string;
    message: string;
    okText?: string;
    cancelText?: string;
    kind?: 'default' | 'danger';
  }): Promise<boolean> {
    if (this.confirmResolver) {
      this.confirmResolver(false);
      this.confirmResolver = null;
    }

    this.confirmTitle = options.title;
    this.confirmMessage = options.message;
    this.confirmOkText = options.okText ?? 'Yes';
    this.confirmCancelText = options.cancelText ?? 'No';
    this.confirmKind = options.kind ?? 'default';
    this.confirmOpen = true;

    this.cdr.markForCheck();

    return new Promise<boolean>((resolve) => {
      this.confirmResolver = resolve;
    });
  }

  onConfirmOk(): void {
    this.confirmOpen = false;
    const resolver = this.confirmResolver;
    this.confirmResolver = null;
    this.cdr.markForCheck();
    resolver?.(true);
  }

  onConfirmCancel(): void {
    this.confirmOpen = false;
    const resolver = this.confirmResolver;
    this.confirmResolver = null;
    this.cdr.markForCheck();
    resolver?.(false);
  }

  /* =============================
     Initial load
  ============================= */

  private loadInitialData(): void {
    this.departments$ = this.holidayService.getAllDepartmentOptions().pipe(
      tap((departments) => {
        this.departments = departments;
        this.updateScopeLabel();
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Departments load error:', err);
        this.showToast('Failed to load departments', 'error');
        return of([] as HolidayDepartmentOption[]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.weeks$ = this.holidayService.getYearWeekOptions(this.currentYear).pipe(
      tap((weeks) => {
        this.weeks = weeks;
        this.updateSelectedWeekLabel();
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('YYWW load error:', err);
        this.showToast('Failed to load weeks', 'error');
        return of([] as HolidayWeekOption[]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.withBusy(forkJoin([this.departments$, this.weeks$]))
      .pipe(
        tap(() => {
          this.updateSelectedWeekLabel();
          this.updateScopeLabel();
          this.cdr.markForCheck();
        }),
        catchError((err) => {
          console.error('Holiday initial load error:', err);
          return of(null);
        }),
      )
      .subscribe();
  }

  /* =============================
     Calendar
  ============================= */

  private syncPickerToCalendar(): void {
    this.pickerYear = this.calendarMonth.year();
    this.pickerMonthIndex = this.calendarMonth.month();
  }

  openMonthPicker(): void {
    this.isMonthPickerOpen = true;
    this.syncPickerToCalendar();
    this.cdr.markForCheck();
  }

  closeMonthPicker(): void {
    this.isMonthPickerOpen = false;
    this.cdr.markForCheck();
  }

  getPickerYears(): number[] {
    const nowYear = dayjs().year();
    const endYear = nowYear + this.maxFutureYears;
    const years: number[] = [];

    for (let y = nowYear; y <= endYear; y += 1) {
      years.push(y);
    }

    return years;
  }

  getPickerMonthsForYear(year: number): Array<{ index: number; label: string }> {
    const months: Array<{ index: number; label: string }> = [];

    for (let i = 0; i < 12; i += 1) {
      const m = dayjs().year(year).month(i).startOf('month');
      months.push({
        index: i,
        label: m.format('MMM'),
      });
    }

    return months;
  }

  selectPickerYear(year: number): void {
    this.pickerYear = year;
    this.cdr.markForCheck();
  }

  applyPickerMonth(monthIndex: number): void {
    this.calendarMonth = dayjs().year(this.pickerYear).month(monthIndex).startOf('month');
    this.rebuildCalendarCells();
    this.isMonthPickerOpen = false;
    this.syncPickerToCalendar();
    this.cdr.markForCheck();
  }

  prevMonth(): void {
    this.calendarMonth = this.calendarMonth.subtract(1, 'month').startOf('month');
    this.rebuildCalendarCells();
    this.syncPickerToCalendar();
    this.cdr.markForCheck();
  }

  nextMonth(): void {
    this.calendarMonth = this.calendarMonth.add(1, 'month').startOf('month');
    this.rebuildCalendarCells();
    this.syncPickerToCalendar();
    this.cdr.markForCheck();
  }

  private rebuildCalendarCells(): void {
    const firstOfMonth = this.calendarMonth.startOf('month');
    const startGrid = firstOfMonth.subtract(firstOfMonth.day(), 'day');

    const cells: Array<dayjs.Dayjs | null> = [];
    for (let i = 0; i < 42; i += 1) {
      cells.push(startGrid.add(i, 'day'));
    }

    this.calendarCells = cells;
  }

  selectDay(day: dayjs.Dayjs): void {
    const selected = day.startOf('day');
    this.form.appliedDate = selected.format('YYYY-MM-DD');
    this.updateSelectedWeekLabel();
    this.cdr.markForCheck();
  }

  isSelectedDay(day: dayjs.Dayjs): boolean {
    return !!this.form.appliedDate && day.format('YYYY-MM-DD') === this.form.appliedDate;
  }

  /* =============================
     Form
  ============================= */

  private buildDefaultForm(): HolidayFormVm {
    return {
      appliedDate: '',
      hours: 8,
      departmentIds: [],
    };
  }

  resetForm(): void {
    this.form = this.buildDefaultForm();
    this.selectedWeekLabel = '';
    this.updateScopeLabel();
    this.cdr.markForCheck();
  }

  async resetFormWithConfirm(): Promise<void> {
    const hasValues =
      !!this.form.appliedDate || this.form.hours !== 8 || this.form.departmentIds.length > 0;

    if (!hasValues) {
      this.clearToast();
      this.resetForm();
      return;
    }

    const ok = await this.openConfirm({
      title: 'Reset form?',
      message: 'This will clear the selected date, hours, and departments.',
      okText: 'Reset',
      cancelText: 'Keep editing',
      kind: 'danger',
    });

    if (!ok) return;

    this.clearToast();
    this.resetForm();
  }

  /* =============================
     Week helpers
  ============================= */

  private updateSelectedWeekLabel(): void {
    if (!this.form.appliedDate) {
      this.selectedWeekLabel = '';
      return;
    }

    const selected = dayjs(this.form.appliedDate).startOf('day');

    const match =
      this.weeks.find((week) => {
        const start = dayjs(week.startDate).startOf('day');
        const end = dayjs(week.endDate).startOf('day');

        return (
          selected.isSame(start, 'day') ||
          selected.isSame(end, 'day') ||
          (selected.isAfter(start, 'day') && selected.isBefore(end, 'day'))
        );
      }) ?? null;

    this.selectedWeekLabel = match ? match.label : '';
  }

  /* =============================
     Department helpers
  ============================= */

  toggleDepartment(departmentId: number): void {
    const exists = this.form.departmentIds.includes(departmentId);

    this.form.departmentIds = exists
      ? this.form.departmentIds.filter((id) => id !== departmentId)
      : [...this.form.departmentIds, departmentId];

    this.form.departmentIds = [...this.form.departmentIds].sort((a, b) => a - b);
    this.updateScopeLabel();
    this.cdr.markForCheck();
  }

  isDepartmentSelected(departmentId: number): boolean {
    return this.form.departmentIds.includes(departmentId);
  }

  clearDepartmentSelection(): void {
    this.form.departmentIds = [];
    this.showDepartmentPicker = false;
    this.updateScopeLabel();
    this.cdr.markForCheck();
  }

  selectAllDepartments(): void {
    this.form.departmentIds = this.departments.map((department) => department.id);
    this.updateScopeLabel();
    this.cdr.markForCheck();
  }

  get selectedDepartmentNames(): string[] {
    if (!this.form.departmentIds.length) return [];

    const selectedIds = new Set(this.form.departmentIds);

    return this.departments
      .filter((department) => selectedIds.has(department.id))
      .map((department) => department.label);
  }

  get selectedDepartmentCount(): number {
    return this.form.departmentIds.length;
  }

  get areAllDepartmentsSelected(): boolean {
    return (
      this.departments.length > 0 && this.form.departmentIds.length === this.departments.length
    );
  }

  private updateScopeLabel(): void {
    if (!this.form.departmentIds.length) {
      this.selectedScopeLabel = 'All departments';
      return;
    }

    if (this.form.departmentIds.length === 1) {
      this.selectedScopeLabel = '1 department selected';
      return;
    }

    this.selectedScopeLabel = `${this.form.departmentIds.length} departments selected`;
  }

  /* =============================
     Validation / submit
  ============================= */

  private validateForm(): string | null {
    if (!this.form.appliedDate) {
      return 'Please select a holiday date.';
    }

    if (this.form.hours == null || Number.isNaN(Number(this.form.hours))) {
      return 'Please enter holiday hours.';
    }

    const hours = Number(this.form.hours);

    if (hours <= 0) {
      return 'Holiday hours must be greater than 0.';
    }

    if (hours > 24) {
      return 'Holiday hours cannot be greater than 24.';
    }

    return null;
  }

  get canSubmit(): boolean {
    return !this.isBusy && !this.validateForm();
  }

  private buildCreateBody(): CreateHolidayBookedHoursBody {
    return {
      departmentIds: [...this.form.departmentIds],
      appliedDate: this.form.appliedDate,
      hours: Number(this.form.hours),
    };
  }

  async submitHoliday(): Promise<void> {
    this.clearToast();

    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showToast(validationMessage, 'error');
      return;
    }

    const body = this.buildCreateBody();
    const scopeText =
      body.departmentIds.length > 0
        ? `${body.departmentIds.length} department${body.departmentIds.length === 1 ? '' : 's'}`
        : 'all departments';

    const ok = await this.openConfirm({
      title: 'Book holiday hours?',
      message: `This will book ${body.hours} holiday hour${body.hours === 1 ? '' : 's'} for ${body.appliedDate} for ${scopeText}.`,
      okText: 'Book holiday',
      cancelText: 'Cancel',
      kind: 'default',
    });

    if (!ok) return;

    this.withBusy(this.holidayService.createHolidayBookedHours(body))
      .pipe(
        tap(() => {
          this.showToast('Holiday booked successfully.', 'success');
          this.resetForm();
        }),
        catchError((err) => {
          console.error('Create holiday booked hours error:', err);
          this.showToast('Failed to book holiday hours', 'error');
          return of(null);
        }),
      )
      .subscribe();
  }

  /* =============================
     Template helpers
  ============================= */

  get selectedDateDisplay(): string {
    if (!this.form.appliedDate) return '';
    return dayjs(this.form.appliedDate).format('dddd, MMMM D, YYYY');
  }

  trackDepartment(_: number, item: HolidayDepartmentOption): number {
    return item.id;
  }
}
